import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { GridRowModes } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";

import { getData } from "../../store/dataSlice";
import { API_URL, LIMIT } from "../../config/config.data";

// renders an autocomplete component for selecting project sites
export default function CategorialEditCellMultiSelect({
  id,
  field,
  column,
  row,
  rowModesModel,
  setRowModesModel,
}) {
  const {
    categoriesEndpoint,
    categoriesEndpointResponseKey,
    categoriesEndpointResponseOptionsKey,
  } = column.renderEditCellProps;

  const dispatch = useDispatch();
  const entity = useSelector((state) => state.dataExplorer.selectedEntity);
  const previousOptions = row[field];
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);

  // get categorical options
  const getOptions = async (abortController) => {
    try {
      const response = await axios.get(
        `${API_URL}/${categoriesEndpoint}?limit=${LIMIT}`,
        {
          signal: abortController.signal,
        }
      );

      setOptions([
        ...response.data[categoriesEndpointResponseKey].sort((a, b) =>
          a[categoriesEndpointResponseOptionsKey].localeCompare(
            b[categoriesEndpointResponseOptionsKey]
          )
        ),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  const addOne = async (optionId) => {
    try {
      axios
        .put(`${API_URL}/${entity}/${id}/${field}/${optionId}`)
        .then((response) => {
          dispatch(getData({dataId: entity, source: "CategoricalEditMulti"}));
        })
        .catch((error) => {
          console.error(`failed to add ${field} to ${entity}`, error);
        });
    } catch (error) {
      console.error(`failed to add ${field} to ${entity}`, error);
    }
  };

  const deleteOne = async (optionId) => {
    try {
      axios
        .delete(`${API_URL}/${entity}/${id}/${field}/${optionId}`)
        .then((response) => {
          dispatch(getData({dataId: entity, source: "CatEditMulti"}));
        })
        .catch((error) => {
          console.error(`failed to delete ${field} from ${entity}`, error);
        });
    } catch (error) {
      console.error(`failed to delete ${field} from ${entity}`, error);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setRowModesModel({
      ...rowModesModel,
      [row.id]: { mode: GridRowModes.View },
    });
  };

  useEffect(() => {
    const abortController = new AbortController();
    getOptions(abortController);
    return () => abortController.abort();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Typography
          sx={{
            width: "200px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {previousOptions
            .map((option) => option[categoriesEndpointResponseOptionsKey])
            .join(", ")}
        </Typography>
        <IconButton onClick={() => setOpen(true)}>
          <EditIcon />
        </IconButton>
      </Box>
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {`Edit ${column.headerName}`}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            id={field}
            options={options}
            limitTags={5}
            getOptionLabel={(option) =>
              option[categoriesEndpointResponseOptionsKey]
            }
            defaultValue={previousOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              // newValue is an array of selected options
              newValue.forEach((option) => {
                if (!previousOptions.includes(option)) {
                  addOne(option.id);
                }
              });

              previousOptions.forEach((option) => {
                if (!newValue.includes(option)) {
                  deleteOne(option.id);
                }
              });
            }}
            renderInput={(params) => (
              <Box sx={{ my: 1 }}>
                <TextField {...params} label={column.headerName} />
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseDialog}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
